// Universal search — the dispatcher's command line.
// Type "45" or "C45" → gate. Type a name → driver. Type a flight # → flight.
// Type a tug id → tug. Press Enter to focus the top result.
//
// Sits in the top-center of the map, frosted glass, high z-index. Dropdown
// shows up to 8 ranked matches; click or arrow-key to select.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, InputBase, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FlightIcon from '@mui/icons-material/Flight';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PinDropIcon from '@mui/icons-material/PinDrop';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { useIntel } from './IntelligenceContext';
import { GATES, DRIVERS, GATE_STATE } from './mockLiveState';
import { GATE_DETAILS } from './mockGateDetails';
import { C } from '../styles';

// Build a single flat searchable index.
function buildIndex() {
  const out = [];
  GATES.forEach(g => {
    const state = GATE_STATE.get(g.id);
    out.push({
      kind: 'gate',
      id: g.id,
      label: g.id,
      sub: state?.flightNumber
        ? `${state.flightNumber} · ${state.origin || ''}`
        : 'Empty',
      keywords: [g.id, String(g.num), `gate ${g.num}`],
    });
  });
  DRIVERS.forEach(d => {
    out.push({
      kind: 'driver',
      id: d.id,
      label: d.name,
      sub: `Badge ${d.badge} · Seniority ${d.seniority}`,
      keywords: [d.name, String(d.badge), d.id],
    });
  });
  // Flights — derived from gate state
  const flights = new Set();
  GATE_STATE.forEach(s => { if (s.flightNumber) flights.add(s.flightNumber); });
  flights.forEach(f => {
    // Find the hosting gate
    let hostGate = null;
    for (const [gid, s] of GATE_STATE.entries()) {
      if (s.flightNumber === f) { hostGate = gid; break; }
    }
    out.push({
      kind: 'flight',
      id: f,
      label: f,
      sub: hostGate ? `at ${hostGate}` : 'unassigned',
      keywords: [f, f.replace(/^WN\s*/, '')],
    });
  });
  // Tugs — derived from gate details
  GATE_DETAILS.forEach(d => {
    if (d.tug) {
      out.push({
        kind: 'tug',
        id: d.tug,
        label: d.tug,
        sub: `at ${d.gateId} · ${d.flightNumber}`,
        keywords: [d.tug, d.tug.replace(/^T-/, '')],
      });
    }
  });
  return out;
}

const INDEX = buildIndex();

function score(item, q) {
  const ql = q.toLowerCase().trim();
  if (!ql) return 0;
  let best = 0;
  for (const k of item.keywords) {
    const kl = String(k).toLowerCase();
    if (kl === ql) best = Math.max(best, 100);
    else if (kl.startsWith(ql)) best = Math.max(best, 70);
    else if (kl.includes(ql)) best = Math.max(best, 40);
  }
  return best;
}

const KIND_META = {
  gate:   { icon: PinDropIcon,        label: 'Gate',   color: '#F9B612' },
  driver: { icon: PersonIcon,         label: 'Driver', color: '#00BCD4' },
  flight: { icon: FlightIcon,         label: 'Flight', color: '#2196F3' },
  tug:    { icon: LocalShippingIcon,  label: 'Tug',    color: '#9C27B0' },
};

function SearchBar() {
  const intel = useIntel();
  const { setSelected } = intel;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);     // highlighted result index
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    return INDEX
      .map(it => ({ it, s: score(it, query) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 8)
      .map(x => x.it);
  }, [query]);

  useEffect(() => { setHi(0); }, [query]);

  // Cmd/Ctrl-K to focus
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Click-outside closes the dropdown
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const select = (item) => {
    setSelected({ kind: item.kind, id: item.id });
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(results.length - 1, h + 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
    if (e.key === 'Enter')     { e.preventDefault(); select(results[hi]); }
  };

  return (
    <Box
      ref={wrapperRef}
      data-intel-overlay
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        position: 'relative',
        flex: 1,
        maxWidth: 460,
        minWidth: 240,
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        backgroundColor: 'rgba(15, 18, 28, 0.95)',
        border: `1px solid ${open ? C.accent : C.border}`,
        borderRadius: open && results.length > 0 ? '6px 6px 0 0' : '6px',
        px: 1.5, py: 0.85,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        transition: 'border-color 90ms',
      }}>
        <SearchIcon sx={{ fontSize: 16, color: open ? C.accent : C.textMuted }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search gates, drivers, flights, tugs…   ⌘K"
          sx={{
            flex: 1, color: C.textPrimary, fontSize: '0.85rem',
            '& input::placeholder': { color: C.textMuted, opacity: 1 },
          }}
        />
        {query && (
          <Chip
            size="small" label={`${results.length}`}
            sx={{
              height: 18, fontSize: '0.6rem', fontWeight: 700,
              color: C.textMuted, backgroundColor: 'rgba(255,255,255,0.08)',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        )}
      </Box>

      {open && results.length > 0 && (
        <Box sx={{
          backgroundColor: 'rgba(15, 18, 28, 0.97)',
          border: `1px solid ${C.accent}`,
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          maxHeight: 380,
          overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          {results.map((r, i) => {
            const meta = KIND_META[r.kind];
            const Icon = meta.icon;
            const isHi = i === hi;
            return (
              <Box
                key={`${r.kind}-${r.id}`}
                onMouseEnter={() => setHi(i)}
                onClick={() => select(r)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 1,
                  cursor: 'pointer',
                  backgroundColor: isHi ? 'rgba(249, 182, 18, 0.10)' : 'transparent',
                  borderLeft: `3px solid ${isHi ? C.accent : 'transparent'}`,
                  borderBottom: `1px solid ${C.border}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Icon sx={{ fontSize: 16, color: meta.color }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: '0.8rem', color: C.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
                    {r.label}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '0.65rem', color: C.textMuted, lineHeight: 1.1 }}>
                    {r.sub}
                  </Typography>
                </Box>
                <Chip
                  size="small" label={meta.label.toUpperCase()}
                  sx={{
                    height: 16, fontSize: '0.55rem', fontWeight: 800, letterSpacing: 0.5,
                    color: meta.color, backgroundColor: 'rgba(255,255,255,0.06)',
                    border: `1px solid ${meta.color}33`,
                    '& .MuiChip-label': { px: 0.6 },
                  }}
                />
                {isHi && (
                  <KeyboardReturnIcon sx={{ fontSize: 12, color: C.accent }} />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default SearchBar;
